import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import vaulttableService from '../services/vaulttableService';
import vaulttablecolumnService from '../services/vaulttablecolumnService';
import vaultsourceService from '../services/vaultsourceService';
import './VaultTableForm.css';

function VaultTableForm({ mode = 'create' }) {
  const navigate = useNavigate();
  const { id, vaultsourceid } = useParams();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [vaulttablecolumns, setVaultTableColumns] = useState([]);
  const [vaultSourceName, setVaultSourceName] = useState('');
  const [formData, setFormData] = useState({
    vaultsourceid: '',
    name: '',
    extract: 'N'
  });

  const isViewMode = mode === 'view';
  const isEditMode = mode === 'edit';
  const isCreateMode = mode === 'create';

  useEffect(() => {
    // Auto-populate vaultsourceid if creating from vault source page
    if (vaultsourceid && isCreateMode) {
      console.log('Setting vaultsourceid from params:', vaultsourceid);
      setFormData(prev => ({ ...prev, vaultsourceid: parseInt(vaultsourceid) }));
    }
  }, [vaultsourceid, isCreateMode]);

  useEffect(() => {
    if ((isEditMode || isViewMode) && id) {
      fetchVaultTable();
      fetchVaultTableColumns();
    }
  }, [id, isEditMode, isViewMode]);

  useEffect(() => {
    // Fetch vault source when vaultsourceid is set
    if (formData.vaultsourceid && (isEditMode || isViewMode)) {
      fetchVaultSource(formData.vaultsourceid);
    }
  }, [formData.vaultsourceid, isEditMode, isViewMode]);

  const fetchVaultSource = async (vaultsourceid) => {
    try {
      const response = await vaultsourceService.getVaultSourceById(vaultsourceid);
      const vaultsourceData = response.data || response;
      setVaultSourceName(vaultsourceData.name || '');
    } catch (err) {
      console.error('Failed to fetch vault source:', err);
    }
  };

  const fetchVaultTable = async () => {
    try {
      setLoading(true);
      const response = await vaulttableService.getVaultTableById(id);
      const vaulttableData = response.data || response;
      setFormData(vaulttableData);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch vault system table');
    } finally {
      setLoading(false);
    }
  };

  const fetchVaultTableColumns = async () => {
    try {
      const response = await vaulttablecolumnService.getAllVaultTableColumns({ vaulttableid: id });
      setVaultTableColumns(response.vaulttablecolumns || []);
    } catch (err) {
      console.error('Failed to fetch vault system table columns:', err);
    }
  };

  const handleDeleteVaultTableColumn = async (columnId) => {
    if (!window.confirm('Are you sure you want to delete this vault system table column?')) {
      return;
    }

    try {
      await vaulttablecolumnService.deleteVaultTableColumn(columnId);
      // Refresh the vault system table columns list
      fetchVaultTableColumns();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete vault system table column');
    }
  };

  const handleToggleEncrypted = async (columnId, currentValue) => {
    try {
      const newValue = currentValue === 'Y' ? 'N' : 'Y';

      // Find the vault system table column to get all its data
      const vaulttablecolumn = vaulttablecolumns.find(vtc => vtc.id === columnId);
      if (!vaulttablecolumn) return;

      // Update the backend
      await vaulttablecolumnService.updateVaultTableColumn(columnId, {
        ...vaulttablecolumn,
        encrypted: newValue
      });

      // Refresh the vault system table columns list
      fetchVaultTableColumns();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update encrypted field');
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    // Validate required fields
    if (!formData.vaultsourceid) {
      setError('Vault System ID is required');
      setLoading(false);
      return;
    }

    if (!formData.name) {
      setError('Name is required');
      setLoading(false);
      return;
    }

    try {
      console.log('Submitting vault system table data:', formData);

      if (isCreateMode) {
        const response = await vaulttableService.createVaultTable(formData);
        console.log('Vault table created:', response);
      } else if (isEditMode) {
        const response = await vaulttableService.updateVaultTable(id, formData);
        console.log('Vault table updated:', response);
      }

      // Navigate back to vault source view
      const targetVaultSourceId = vaultsourceid || formData.vaultsourceid;
      if (targetVaultSourceId) {
        navigate(`/admin/vaults/${targetVaultSourceId}`);
      } else {
        navigate('/admin/vaults');
      }
    } catch (err) {
      console.error('Error saving vault system table:', err);
      setError(err.response?.data?.message || err.message || 'Failed to save vault system table');
      setLoading(false);
    }
  };

  const handleCancel = () => {
    const targetVaultSourceId = vaultsourceid || formData.vaultsourceid;
    if (targetVaultSourceId) {
      navigate(`/admin/vaults/${targetVaultSourceId}`);
    } else {
      navigate('/admin/vaults');
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this vault system table?')) {
      return;
    }

    try {
      setLoading(true);
      await vaulttableService.deleteVaultTable(id);
      const targetVaultSourceId = vaultsourceid || formData.vaultsourceid;
      if (targetVaultSourceId) {
        navigate(`/admin/vaults/${targetVaultSourceId}`);
      } else {
        navigate('/admin/vaults');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete vault system table');
      setLoading(false);
    }
  };

  if (loading && (isEditMode || isViewMode)) {
    return <div className="vaulttable-form-container">Loading vault system table...</div>;
  }

  return (
    <div className="vaulttable-form-container">
      {/* Breadcrumb */}
      {(isViewMode || isEditMode) && (vaultSourceName || formData.name) && (
        <div className="breadcrumb">
          <Link to="/admin/vaults" className="breadcrumb-link">
            Vault Systems
          </Link>
          <span className="breadcrumb-separator">/</span>
          {vaultSourceName && formData.vaultsourceid && (
            <>
              <Link to={`/admin/vaults/${formData.vaultsourceid}`} className="breadcrumb-link">
                {vaultSourceName}
              </Link>
              <span className="breadcrumb-separator">/</span>
            </>
          )}
          <span className="breadcrumb-current">{formData.name}</span>
        </div>
      )}

      <h2>
        {isCreateMode && 'Create New Vault System Table'}
        {isEditMode && 'Edit Vault System Table'}
        {isViewMode && 'View Vault System Table'}
      </h2>

      {error && <div className="error-message">{error}</div>}

      <form onSubmit={handleSubmit}>
        {/* Basic Information */}
        <div className="form-section">
          <h3>Basic Information</h3>

          <div className="form-group">
            <label htmlFor="name">Name *</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              disabled={isViewMode}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="extract">Extract</label>
            <select
              id="extract"
              name="extract"
              value={formData.extract}
              onChange={handleChange}
              disabled={isViewMode}
            >
              <option value="Y">Yes (Y)</option>
              <option value="N">No (N)</option>
            </select>
          </div>

          {(isViewMode || isEditMode) && (
            <div className="form-group">
              <label htmlFor="partition_date_column_id">Partition Date Column</label>
              <select
                id="partition_date_column_id"
                name="partition_date_column_id"
                value={formData.partition_date_column_id || ''}
                onChange={(e) => setFormData(prev => ({ ...prev, partition_date_column_id: e.target.value ? parseInt(e.target.value) : null }))}
                disabled={isViewMode}
              >
                <option value="">— None —</option>
                {vaulttablecolumns.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
              <small className="form-hint">
                Date column used to derive the <code>year</code>/<code>month</code> partition for the parquet output and Athena table. Leave blank if this table isn't date-partitioned.
              </small>
            </div>
          )}
        </div>

        {/* Form Actions */}
        <div className="form-actions">
          {isViewMode ? (
            <>
              <button type="button" onClick={() => navigate(`/admin/vaults/${vaultsourceid || formData.vaultsourceid}/vaulttable/${id}/edit`)} title="Edit Vault System Table">
                ✏️ Edit
              </button>
              <button type="button" onClick={handleDelete} className="btn-danger" title="Delete Vault System Table">
                🗑️ Delete
              </button>
              <button type="button" onClick={handleCancel} title="Back to Vault System">
                ← Back to Vault System
              </button>
            </>
          ) : (
            <>
              <button type="submit" disabled={loading}>
                {loading ? 'Saving...' : isCreateMode ? 'Create Vault System Table' : 'Update Vault System Table'}
              </button>
              <button type="button" onClick={handleCancel} disabled={loading} title="Cancel">
                ← Cancel
              </button>
            </>
          )}
        </div>
      </form>

      {/* Vault System Table Columns Section - Only show in view/edit mode */}
      {(isViewMode || isEditMode) && id && (
        <div className="vaulttablecolumns-section">
          <div className="vaulttablecolumns-header">
            <h3>Vault System Table Columns</h3>
            <Link to={`/admin/vaults/${vaultsourceid || formData.vaultsourceid}/vaulttable/${id}/vaulttablecolumn/new`} className="btn-add-vaulttablecolumn" title="Add Vault System Table Column">
              ➕ Add Vault System Table Column
            </Link>
          </div>

          {vaulttablecolumns.length === 0 ? (
            <p className="no-vaulttablecolumns">No vault system table columns found.</p>
          ) : (
            <>
              <div className="vaulttablecolumns-list">
                {vaulttablecolumns
                  .map((vaulttablecolumn) => (
                    <div
                      key={vaulttablecolumn.id}
                      className="vaulttablecolumn-card clickable-card"
                      onClick={() => navigate(`/admin/vaults/${vaultsourceid || formData.vaultsourceid}/vaulttable/${id}/vaulttablecolumn/${vaulttablecolumn.id}`)}
                      title="Click to view"
                    >
                      <div className="vaulttablecolumn-info">
                        <div className="vaulttablecolumn-name">{vaulttablecolumn.name}</div>
                        <div className="vaulttablecolumn-flags">
                          <div className="toggle-group">
                            <label className="toggle-label">Encrypted:</label>
                            <button
                              className={`toggle-button ${vaulttablecolumn.encrypted === 'Y' ? 'toggle-yes' : 'toggle-no'}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleToggleEncrypted(vaulttablecolumn.id, vaulttablecolumn.encrypted);
                              }}
                              title="Toggle Encrypted"
                            >
                              {vaulttablecolumn.encrypted === 'Y' ? 'Yes' : 'No'}
                            </button>
                          </div>
                        </div>
                      </div>
                      <div className="vaulttablecolumn-actions">
                        <Link
                          to={`/admin/vaults/${vaultsourceid || formData.vaultsourceid}/vaulttable/${id}/vaulttablecolumn/${vaulttablecolumn.id}/edit`}
                          className="btn-edit"
                          onClick={(e) => e.stopPropagation()}
                          title="Edit"
                        >
                          ✏️
                        </Link>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteVaultTableColumn(vaulttablecolumn.id);
                          }}
                          className="btn-delete"
                          title="Delete"
                        >
                          🗑️
                        </button>
                      </div>
                    </div>
                  ))}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default VaultTableForm;
